/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable react-hooks/exhaustive-deps */
import { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { plainToClass } from 'class-transformer';
import { useInject } from '../ioc';
import { QueryService } from './query.service';
const isNothing = (obj) => !obj || obj === '0' || obj === 'false';
/**
 * @example
 * ```
 * flattenObject({
 *  a: {
 *    b: 'b1',
 *    c: {
 *      d: 'd1'
 *    }
 *  }
 *})
 * => {
 *  'a.b': 'b1',
 *  'a.c': 'd1',
 *}
 * ```
 *
 * @remarks
 *
 * The implementation is really fast,
 * it does not create too many intermediate objects
 */
function flattenObject(head) {
    function dfsFlatten(node, path, result) {
        for (const key in node) {
            if (!Object.prototype.hasOwnProperty.call(node, key) || isNothing(key))
                continue;
            path.push(key);
            const element = node[key];
            if (typeof element === 'object') {
                dfsFlatten(element, path, result);
            }
            else {
                // base case
                result[path.join('.')] = element;
            }
            path.pop();
        }
    }
    const result = {};
    dfsFlatten(head, [], result);
    return result;
}
/** Хук, который возвращает массив, где
 * первый элемент - объектное представление query string
 * второй элемент - метод, обновляющий в браузере query string на основе переданного ему объекта
 * */
export function useQuery(constructor, router, namespace) {
    const navigate = useNavigate();
    const location = useLocation();
    const query = useInject(QueryService);
    const result = query.parse(location.search.split('#')[0]);
    const filteredResult = query.parse(location.search.split('#')[0], namespace);
    const setQuery = (update) => {
        if (namespace && update.filters) {
            Object.entries(update.filters).forEach(([key, value]) => {
                if (!value ||
                    (Array.isArray(value) && !value.length) ||
                    value === '0') {
                    Object.keys(result).forEach((resultKey) => {
                        if (resultKey === `${namespace}_filter_${key}`)
                            delete result[resultKey];
                    });
                }
                if (value && typeof value === 'object') {
                    const filterValues = Object.values(value);
                    if (!filterValues.length ||
                        (filterValues.length && filterValues.every((item) => !item))) {
                        Object.keys(result).forEach((resultKey) => {
                            if (resultKey === `${namespace}_filter_${key}`)
                                delete result[resultKey];
                        });
                    }
                }
            });
        }
        const filteredResultWithTags = query.parse(query.stringify(filteredResult, namespace));
        const fieldsOutsideNamespace = Object.entries(result)
            .filter(([key]) => !(key in filteredResultWithTags))
            .reduce((acc, [key, value]) => (Object.assign(Object.assign({}, acc), { [key]: value })), {});
        const nextQuery = Object.assign(Object.assign(Object.assign({}, (namespace ? filteredResult : result)), update), (update.filters
            ? {
                filters: Object.assign(Object.assign({}, ((result && result.filters) || {})), update.filters),
            }
            : {}));
        for (const key in nextQuery) {
            if (key && !nextQuery[key]) {
                delete nextQuery[key];
            }
            if ((key === 'filters' &&
                typeof nextQuery[key] === 'object' &&
                !Array.isArray(nextQuery[key])) ||
                nextQuery[key] === null) {
                for (const filterKey in nextQuery[key]) {
                    if (!filterKey)
                        continue;
                    if (isNothing(nextQuery[key][filterKey])) {
                        delete nextQuery[key][filterKey];
                    }
                    if (Array.isArray(nextQuery[key][filterKey])) {
                        nextQuery[key][filterKey] = nextQuery[key][filterKey].filter((value) => value && value !== '0');
                        // eslint-disable-next-line max-depth
                        if (!nextQuery[key][filterKey].length) {
                            delete nextQuery[key][filterKey];
                        }
                    }
                    else if (typeof nextQuery[key][filterKey] === 'object') {
                        const flattenParams = flattenObject({
                            [filterKey]: nextQuery[key][filterKey],
                        });
                        delete nextQuery[key][filterKey];
                        Object.assign(nextQuery[key], flattenParams);
                    }
                }
            }
        }
        const newQuery = namespace && Object.keys(fieldsOutsideNamespace).length !== 0
            ? `${query.stringify(fieldsOutsideNamespace)}&${query.stringify(nextQuery, namespace)}`
            : query.stringify(nextQuery, namespace);
        if (router === null || router === void 0 ? void 0 : router.goToList) {
            router.goToList(newQuery);
        }
        else {
            navigate(Object.assign(Object.assign({}, location), { search: newQuery }));
        }
    };
    const classInstance = useMemo(() => plainToClass(constructor, namespace ? filteredResult : result), [location.search]);
    return [classInstance, setQuery];
}
